import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Label, Skeleton, Spinner } from "@lemonadetraining/web-ui";
import {
  useAppDispatch,
  useAppSelector,
  cartActions,
  checkout,
} from "@lemonadetraining/web-shared";
import {
  COMMITMENT_CHECKOUT_COPY,
  isCommitmentLine,
  type MembershipCartLine,
} from "@lemonadetraining/web-shared/utils/commitmentPlans";
import {
  CONSENT_RECOVERY_MESSAGE,
  consentEchoPayload,
  consentRecoveryFor,
  CONSENT_REASON,
  GYM_TERMS_RECOVERY_MESSAGE,
  gymTermsRecoveryFor,
  isRenderableGymTermsRefusal,
} from "@lemonadetraining/web-shared/utils/commitmentConsent";
import type {
  GymTermsAcceptanceScope,
  GymTermsRefusalDetails,
  GymTermsSummary,
} from "@lemonadetraining/contracts";
import MyModal from "./MyModal";
import CartItem from "./CartItem";
import CommitmentReviewBlock from "./CommitmentReviewBlock";
import MonthlyReviewBlock from "./MonthlyReviewBlock";
import { useCheckoutReview } from "../hooks/useCheckoutReview";

type PublishedGymTerms = Extract<GymTermsSummary, { hasTerms: true }>;

/* Compiler-bound to the published union rather than written as a bare literal,
   so a renamed scope fails to build instead of silently never matching. */
const ALL_PAID_SCOPE: GymTermsAcceptanceScope = "all_paid";

/**
 * Review and confirm.
 *
 * An ordinary cart is unchanged from what it has always been: the items, a
 * total, a button. A cart holding a commitment membership gets the step the law
 * and plain decency both require — what the term costs in full, what it saves,
 * when it ends, what happens then, and an un-pre-ticked confirmation of the
 * exact consent wording the SERVER published, echoed back by hash.
 *
 * Two rules run through everything below:
 *
 *   1. The consent wording is rendered exactly as it was served and is never
 *      retyped, trimmed or transformed. The server hashes the bytes it
 *      published; anything this component "tidies" is a mismatch at best and,
 *      at worst, a hash recorded for text nobody read.
 *   2. The Checkout button is never disabled for want of consent. A disabled
 *      button with no stated reason is a dead end — pressing it must say what
 *      is missing and put the cursor on it.
 */

/** How long to wait before speaking a new total, so a burst of edits is one announcement. */
const TOTAL_ANNOUNCE_DEBOUNCE_MS = 700;

const CONSENT_CHECKBOX_ID = "crd-consent-checkbox";
const CONSENT_ALERT_ID = "crd-consent-alert";

const TERMS_CHECKBOX_ID = "gym-terms-consent-checkbox";
const TERMS_ALERT_ID = "gym-terms-consent-alert";
const TERMS_BLOCKED_ID = "gym-terms-blocked";

/** Two presses of Retry is enough to tell a blip from an outage. */
const TERMS_RETRY_LIMIT = 2;

const GYM_TERMS_COPY = {
  heading: "Your gym's terms",
  consentHeading: "Agree to your gym's terms",
  consentLabel: "I agree to my gym's terms and conditions.",
  blockedRetry: "Try again",
  blockedExhausted:
    "We still can't load your gym's terms. Nothing has been charged — please contact your gym.",
  multiGym:
    "These memberships belong to different gyms, each with its own terms. " +
    "Nothing has been charged — please buy them one gym at a time.",
  /* Deliberately unnamed. The only name a cart line carries is the account
   * display name of whoever created the product, not the gym's — and naming the
   * wrong party is worse than naming none on the screen where someone agrees to
   * terms. The gym's own name heads the page this opens. */
  linkText: "Read your gym's terms and conditions (opens in a new tab)",
};

const CheckoutModal = () => {
  const dispatch = useAppDispatch();
  const cart = useAppSelector((state) => state.cart);
  const cartEmpty = cart.cartItems?.length === 0;
  const open = cart.showCheckout;
  const cartLines = cart.cartItems as MembershipCartLine[];

  const {
    lines,
    monthlyLines,
    productsLoading,
    needsStatutory,
    hasRecurringGymLines,
    copy,
    copyState,
    copyFailureReason,
    refetchReview,
    consentSignature,
    termsByBoxID,
    termsState,
    refetchTerms,
  } = useCheckoutReview(cartLines, open);

  const [consented, setConsented] = useState(false);
  const [termsConsented, setTermsConsented] = useState(false);
  const [termsRefusals, setTermsRefusals] = useState<Record<string, GymTermsRefusalDetails>>({});
  const [termsRetries, setTermsRetries] = useState(0);
  const [termsRetrying, setTermsRetrying] = useState(false);
  const [consentMessage, setConsentMessage] = useState("");
  const [termsMessage, setTermsMessage] = useState("");
  const [totalAnnouncement, setTotalAnnouncement] = useState("");

  const copyReady = copyState === "ready" && copy !== null;
  /* "idle" is loading too: on the first open render neither effect has run yet,
   * and treating that frame as loaded would paint the review from nothing —
   * including a "we couldn't check with your gym" note about a read that has
   * not been attempted. */
  const reviewLoading =
    needsStatutory && (productsLoading || copyState === "loading" || copyState === "idle");
  /* A carted price the gym has since changed makes the carted offer dead: the
   * button and the charge would use the old price while the review states the
   * new one. Nothing sellable is on screen, so there is nothing to consent to. */
  const anyPriceChanged =
    lines.some(({ review }) => review.priceChanged) ||
    monthlyLines.some(({ review }) => review.priceChanged);

  const termsEntries = useMemo(
    () =>
      Object.entries(termsByBoxID).flatMap(([boxID, summary]) =>
        summary.hasTerms ? [{ boxID, publicUrl: summary.publicUrl }] : []
      ),
    [termsByBoxID]
  );

  /* Which gyms' terms this purchase must carry acceptance for. A refusal WINS
     over the summary for the same gym: the gate re-derives the hash from the
     bytes it published, so its values are canonical and the summary may be
     stale — or, in the mixed-fleet case, absent while the server enforces. */
  /* A refusal must not outlive the line that caused it: the buyer can remove a
     line with the modal open, and a stale refusal would then ask them to agree
     to a gym they are no longer buying from. */
  const cartBoxIDs = useMemo(
    () => new Set(cartLines.map((line) => line.boxID).filter(Boolean)),
    [cartLines]
  );

  /* Which gyms this cart holds a COMMITMENT from — the narrow scope, and the
     only purchases a server predating `acceptanceScope` enforces. */
  const commitmentBoxIDs = useMemo(
    () =>
      new Set(
        cartLines
          .filter(isCommitmentLine)
          .map((line) => line.boxID)
          .filter(Boolean)
      ),
    [cartLines]
  );

  const requiredTerms = useMemo(() => {
    /* `acceptanceRequired` is the enforcement flag and knows nothing about the
       cart, so it cannot decide this alone: pairing it with a scope the server
       did not send would draw a checkbox whose tick is recorded nowhere. An
       absent scope reads as the narrow one. */
    const required = Object.entries(termsByBoxID).filter(
      (entry): entry is [string, PublishedGymTerms] =>
        entry[1].hasTerms &&
        entry[1].acceptanceRequired &&
        (entry[1].acceptanceScope === ALL_PAID_SCOPE || commitmentBoxIDs.has(entry[0]))
    );
    const stillCarted = Object.entries(termsRefusals).filter(([boxID]) => cartBoxIDs.has(boxID));
    const byBoxID = new Map(
      [...required, ...stillCarted].map(([boxID, terms]) => [
        boxID,
        {
          boxID,
          termsVersion: terms.termsVersion,
          termsContentHash: terms.termsContentHash,
          publicUrl: terms.publicUrl,
        },
      ])
    );
    return [...byBoxID.values()].sort((a, b) => a.boxID.localeCompare(b.boxID));
  }, [termsByBoxID, termsRefusals, cartBoxIDs, commitmentBoxIDs]);

  /* The claim the server accepts names exactly one gym, so a cart spanning two
     that both require acceptance could never be satisfied by ticking. */
  const termsMultiGym = requiredTerms.length > 1;
  /* Scoping refusals to the cart must never SWALLOW one. A refusal we cannot
     render — a torn payload, or one naming a gym the cart no longer holds —
     contributes to no checkbox, so if it did not also reach the blocked state
     the buyer would press Pay and see nothing at all. */
  const termsUnrenderable = Object.values(termsRefusals).some(
    (refusal) =>
      !isRenderableGymTermsRefusal(refusal) ||
      gymTermsRecoveryFor(refusal.reason) === "blocked" ||
      !cartBoxIDs.has(refusal.boxID)
  );
  const termsBlocked = termsMultiGym || termsUnrenderable;
  const termsBlockedMessage = termsMultiGym
    ? GYM_TERMS_COPY.multiGym
    : termsRetries >= TERMS_RETRY_LIMIT
      ? GYM_TERMS_COPY.blockedExhausted
      : GYM_TERMS_RECOVERY_MESSAGE.blocked;
  /* No link in the multi-gym case: it would belong to one of two gyms with
     nothing on screen saying which. */
  const termsBlockedUrl = termsMultiGym
    ? ""
    : (Object.values(termsRefusals).find(
        (refusal) => refusal.publicUrl !== "" && cartBoxIDs.has(refusal.boxID)
      )?.publicUrl ?? "");
  /* `termsBlocked` opens the gate on its own: a refusal we cannot turn into a
     checkbox still has to be said out loud. */
  const showTermsGate =
    !reviewLoading && !anyPriceChanged && (requiredTerms.length > 0 || termsBlocked);

  /* Deliberately NOT merged into consentSignature: a gym editing its terms must
     reset THIS tick only. Composed over requiredTerms so a refusal's newer
     version, which never reaches the summary, resets it too. */
  const requiredTermsSignature = useMemo(
    () =>
      requiredTerms
        .map((terms) => `${terms.boxID}|${terms.termsVersion}|${terms.termsContentHash}`)
        .join("|"),
    [requiredTerms]
  );

  useEffect(() => {
    setTermsConsented(false);
  }, [requiredTermsSignature]);

  /* Retries are spent against one blockage. Carrying the count into the next
     one would tell a buyer to contact their gym without ever offering them an
     attempt. */
  useEffect(() => {
    if (!termsBlocked) setTermsRetries(0);
  }, [termsBlocked]);

  useEffect(() => {
    if (termsRetrying && termsState !== "loading") {
      setTermsRefusals({});
      setTermsRetrying(false);
    }
  }, [termsRetrying, termsState]);

  const focusTermsCheckbox = useCallback(() => {
    document.getElementById(TERMS_CHECKBOX_ID)?.focus();
  }, []);

  /* The blocked panel has no control to point at, so the panel itself answers
     a press of Pay — focusing it scrolls it into view and gets it read out;
     without that the press is silently inert, which is worse than a disabled
     button. Deferred to an effect because a refusal can CREATE the blocked
     state, and focusing it in the handler would run before it is mounted. */
  const [announceBlocked, setAnnounceBlocked] = useState(false);
  useEffect(() => {
    if (!announceBlocked) return;
    const panel = document.getElementById(TERMS_BLOCKED_ID);
    if (!panel) return;
    panel.focus();
    setAnnounceBlocked(false);
  }, [announceBlocked, termsBlocked, showTermsGate]);

  /* The tick belongs to ONE set of terms. Anything that changes what is being
   * agreed to — a re-fetch of the wording, a different plan, a price the gym
   * moved underneath us — drops it, and the buyer confirms again. */
  useEffect(() => {
    setConsented(false);
  }, [consentSignature]);

  /* The message describes the last attempt to pay, so it does not outlive the
   * session it belongs to. */
  useEffect(() => {
    if (!open) {
      setConsentMessage("");
      setTermsMessage("");
      setTermsRefusals({});
      setTermsRetries(0);
    }
  }, [open]);

  /*
   * Radix renders the checkbox as a button carrying our id. It is reached by id
   * rather than by ref because the shared Checkbox is a plain function
   * component under React 18, where a ref passed to one is dropped with a
   * warning instead of reaching the element.
   */
  const focusConsentCheckbox = useCallback(() => {
    document.getElementById(CONSENT_CHECKBOX_ID)?.focus();
  }, []);

  /*
   * One polite region for the total, mounted empty and debounced.
   *
   * Empty on mount because a live region with text in it can be read out as the
   * dialog opens, announcing a change nobody made; debounced because removing
   * three items should be one "total is now …", not three interruptions on top
   * of the removals the buyer already heard.
   */
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hasOpenedRef.current = false;
      setTotalAnnouncement("");
      return undefined;
    }
    if (!hasOpenedRef.current) {
      hasOpenedRef.current = true;
      return undefined;
    }
    const timer = setTimeout(
      () => setTotalAnnouncement(`Total ${cart.totalPrice} €`),
      TOTAL_ANNOUNCE_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [open, cart.totalPrice]);

  const handleClose = () => {
    dispatch(cartActions.showCheckout(false));
  };

  const handleConsentChange = (checked: boolean | "indeterminate") => {
    setConsented(checked === true);
    if (checked === true) setConsentMessage("");
  };

  /* Only a genuinely unpublished language gets the language answer. Every
   * other failure — including the malformed-argument one, which is a code
   * defect this client cannot actually trigger — gets the honest "we couldn't
   * load them" and a retry, because that is what is true. */
  const localeUnsellable = copyFailureReason === CONSENT_REASON.localeUnsupported;
  const copyFailedMessage = localeUnsellable
    ? CONSENT_RECOVERY_MESSAGE.locale
    : COMMITMENT_CHECKOUT_COPY.copyFailed;

  const handleCheckout = async () => {
    if (needsStatutory) {
      if (reviewLoading || copyState === "loading") {
        // Never send a commitment to Stripe without the terms the buyer was
        // meant to read — and never tell them to confirm a checkbox that is
        // not on screen yet. While EITHER half is still loading, the honest
        // answer is "loading".
        setConsentMessage(COMMITMENT_CHECKOUT_COPY.copyLoading);
        return;
      }
      if (anyPriceChanged) {
        // The carted offer no longer exists at its carted price. The server
        // would refuse it anyway; saying so here, with the way out, beats
        // collecting consent for a purchase that cannot complete. Checked
        // ahead of a failed copy fetch because this is the blocker no retry
        // can clear — lead with the one the buyer can actually act on.
        setConsentMessage(COMMITMENT_CHECKOUT_COPY.staleCommitmentLine);
        return;
      }
      if (!copyReady) {
        // The fetch failed. The same locale-aware message the error box shows
        // (never "try again" for a language that cannot be retried into
        // existence).
        setConsentMessage(copyFailedMessage);
        return;
      }
      if (!consented) {
        // The same sentence the server-refusal path uses: an unticked press and
        // a consent_missing refusal are the same situation to the buyer.
        setConsentMessage(CONSENT_RECOVERY_MESSAGE.reconfirm);
        focusConsentCheckbox();
        return;
      }
    }

    /* Checked after the statutory gate, so a buyer who has answered neither is
       pointed at the first thing to do. */
    if (showTermsGate) {
      if (termsBlocked) {
        // The panel already states the reason, so it is focused rather than
        // echoed into the alert below — same answer, said once.
        setAnnounceBlocked(true);
        return;
      }
      if (!termsConsented) {
        setTermsMessage(GYM_TERMS_RECOVERY_MESSAGE.accept);
        focusTermsCheckbox();
        return;
      }
    }

    setConsentMessage("");
    setTermsMessage("");
    const result = await dispatch(
      // This modal declares itself the handler for consent refusals — even for
      // a cart it did not classify as committing, since the server may know
      // better (stale cart data) and the in-place answer must not compete with
      // a banner naming a checkbox that is not on screen.
      checkout(
        cart.cartItems,
        needsStatutory && copy ? consentEchoPayload(copy) : null,
        /* The server's claim names exactly one gym; multi-gym is blocked above. */
        requiredTerms.length === 1
          ? {
              boxID: requiredTerms[0].boxID,
              termsVersion: requiredTerms[0].termsVersion,
              termsContentHash: requiredTerms[0].termsContentHash,
            }
          : null,
        { handlesConsentRefusals: true }
      )
    );

    /* Never touches setConsented: a gym's terms moving must not disturb the
       statutory tick. */
    const gymTermsRefusal = result?.gymTermsRefusal ?? null;
    if (gymTermsRefusal) {
      setTermsRefusals((prev) => ({ ...prev, [gymTermsRefusal.boxID]: gymTermsRefusal }));
      setTermsConsented(false);
      /* Decide from the state this refusal is ABOUT to produce, not the one on
         screen: a refusal naming a second gym turns the gate into the blocked
         panel, and pointing at a checkbox that is being unmounted would leave
         contradictory text on screen and drop focus to the body. */
      const willBlock =
        gymTermsRecoveryFor(gymTermsRefusal.reason) === "blocked" ||
        !isRenderableGymTermsRefusal(gymTermsRefusal) ||
        !cartBoxIDs.has(gymTermsRefusal.boxID) ||
        new Set([...requiredTerms.map((t) => t.boxID), gymTermsRefusal.boxID]).size > 1;
      if (willBlock) {
        setTermsMessage("");
        setAnnounceBlocked(true);
      } else {
        setTermsMessage(
          GYM_TERMS_RECOVERY_MESSAGE[gymTermsRecoveryFor(gymTermsRefusal.reason) ?? "accept"]
        );
        focusTermsCheckbox();
      }
      return;
    }

    const consentReason = result?.consentReason ?? null;
    const recovery = consentRecoveryFor(consentReason);
    if (recovery === "refetch") {
      // The published terms moved. Fetch them again in place — a reload would
      // take the cart with it, and the buyer did nothing wrong.
      refetchReview();
      setConsented(false);
      setConsentMessage(CONSENT_RECOVERY_MESSAGE.refetch);
    } else if (recovery === "reconfirm") {
      if (needsStatutory) {
        setConsented(false);
        setConsentMessage(CONSENT_RECOVERY_MESSAGE.reconfirm);
        focusConsentCheckbox();
      } else if (hasRecurringGymLines) {
        // The server's gate covers more than the copy this client is holding
        // says — an older revision answered the fetch, or it failed outright.
        // The line is fine; our picture of the scope is what is stale, so read
        // it again rather than telling the buyer to rebuild a healthy cart.
        refetchReview();
        setConsented(false);
        setConsentMessage(CONSENT_RECOVERY_MESSAGE.refetch);
      } else {
        // The server found a commitment line the client's cart data did not
        // show, so no checkbox is on screen — "please confirm" would point at
        // nothing the buyer can act on. The sale stays refused server-side.
        setConsentMessage(COMMITMENT_CHECKOUT_COPY.staleCommitmentLine);
      }
    } else if (recovery === "locale") {
      setConsentMessage(CONSENT_RECOVERY_MESSAGE.locale);
    } else if (consentReason) {
      // A consent refusal we recognize but have no repair for — a code defect,
      // or a token the server started sending after this client shipped. This
      // modal told the thunk it owns consent refusals, so no banner fired; if
      // this branch said nothing the buyer would press Pay and watch
      // absolutely nothing happen. Say the honest thing instead.
      setConsentMessage(COMMITMENT_CHECKOUT_COPY.copyFailed);
    }
  };

  return (
    <MyModal
      open={open}
      onClose={handleClose}
      backgroundColor="hsl(var(--popover))"
      title="Shopping cart"
      showTitle={false}
    >
      <div className="mb-10">
        <p className="text-base font-semibold">Shopping cart</p>
        {cartEmpty && <p className="text-sm text-muted-foreground">Shopping cart empty</p>}
      </div>
      <div className="mb-10">
        {cart.cartItems.map((eachItem) => (
          <CartItem
            key={eachItem.programUID}
            imageSrc={eachItem.coverArt}
            title={eachItem.title}
            id={eachItem.programUID}
            pricing={eachItem.pricing}
            selectedOptionKey={eachItem.selectedOptionKey}
          />
        ))}

        {/* The commitment review, once there is something real to put in it.
            A skeleton rather than a spinner: the shape of what is coming is
            itself information, and this is a step nobody should feel hurried
            through. */}
        {reviewLoading && (
          <div className="mt-4 space-y-2" data-testid="commitment-review-skeleton">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {!reviewLoading &&
          lines.map(({ line, review }) => (
            <CommitmentReviewBlock
              key={line.programUID}
              title={line.title}
              review={review}
              testIdSuffix={line.programUID}
            />
          ))}

        {!reviewLoading &&
          monthlyLines.map(({ line, review }) => (
            <MonthlyReviewBlock
              key={line.programUID}
              title={line.title}
              review={review}
              testIdSuffix={line.programUID}
            />
          ))}

        {/* The withdrawal notice is rendered, not merely hashed. Echoing a hash
            for a disclosure the buyer never saw is exactly the hole this
            closes — the evidence has to describe something that was on screen. */}
        {!reviewLoading && needsStatutory && copyReady && (
          <section
            aria-labelledby="withdrawal-info-heading"
            className="mt-4 rounded-lg border p-4"
            data-testid="withdrawal-info"
          >
            <h3 id="withdrawal-info-heading" className="text-sm font-semibold">
              {COMMITMENT_CHECKOUT_COPY.withdrawalHeading}
            </h3>
            <p className="text-muted-foreground mt-1 text-sm">{copy.withdrawalInfo}</p>
          </section>
        )}

        {/* The terms could not be loaded. Say so plainly and offer the way out,
            rather than leaving a checkout that quietly cannot complete. */}
        {!reviewLoading && needsStatutory && copyState === "error" && (
          <div className="mt-4 rounded-lg border p-4" role="alert" data-testid="consent-copy-error">
            <p className="text-sm">{copyFailedMessage}</p>
            {/* No retry for a language this platform does not publish — pressing
                it again could only fail the same way, which is worse than not
                offering it. */}
            {!localeUnsellable && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={refetchReview}
                data-testid="consent-copy-retry"
              >
                {COMMITMENT_CHECKOUT_COPY.copyRetry}
              </Button>
            )}
          </div>
        )}

        {/* The gate itself. Present only for the purchases the SERVER says its
            own gate covers, and only while the carted offer still exists at its
            carted price. A price the gym has since moved means the purchase
            cannot complete as displayed, and a tick collected against it would
            be consent to a contract nobody can enter. */}
        {!reviewLoading && needsStatutory && copyReady && !anyPriceChanged && (
          <section
            aria-labelledby="consent-heading"
            className="mt-4 rounded-lg border p-4"
            data-testid="consent-gate"
          >
            <h3 id="consent-heading" className="text-sm font-semibold">
              {COMMITMENT_CHECKOUT_COPY.consentHeading}
            </h3>
            <div className="mt-2 flex flex-row items-start gap-3">
              <Checkbox
                id={CONSENT_CHECKBOX_ID}
                data-testid="crd-consent-checkbox"
                className="mt-1"
                checked={consented}
                onCheckedChange={handleConsentChange}
                aria-describedby={CONSENT_ALERT_ID}
              />
              {/* The served string and nothing else: no wrapper that trims it,
                  no casing utility, no markup inside it. */}
              <Label htmlFor={CONSENT_CHECKBOX_ID} className="text-sm leading-relaxed font-normal">
                {copy.consentWording}
              </Label>
            </div>
          </section>
        )}

        {/* The gym's own terms, for any line the gym sells. Additive disclosure,
            so it waits silently rather than showing a skeleton, and a gym
            without terms leaves the checkout exactly as it was. */}
        {!reviewLoading &&
          termsState === "ready" &&
          termsEntries.length > 0 &&
          /* Suppressed when the acceptance gate is showing: that gate carries
             the same link inside its label, and two identical links in adjacent
             boxes reads as a mistake. */
          !showTermsGate && (
            <section
              aria-labelledby="gym-terms-heading"
              className="mt-4 rounded-lg border p-4"
              data-testid="gym-terms-section"
            >
              <h3 id="gym-terms-heading" className="text-sm font-semibold">
                {GYM_TERMS_COPY.heading}
              </h3>
              <ul className="mt-2 space-y-1">
                {termsEntries.map(({ boxID, publicUrl }) => (
                  <li key={boxID}>
                    {/* The server's own URL, never reassembled here: a slug this
                      client guessed could point at another gym's document. */}
                    <a
                      href={publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="gym-terms-link"
                      className="text-sm underline underline-offset-2"
                    >
                      {GYM_TERMS_COPY.linkText}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

        {/* Driven by requiredTerms, so a refusal renders the gate even when the
            summary said nothing was needed — otherwise that buyer dead-ends at
            the pay button. A sibling of the consent gate, never nested in it. */}
        {showTermsGate && (
          <section
            aria-labelledby="gym-terms-consent-heading"
            className="mt-4 rounded-lg border p-4"
            data-testid="gym-terms-consent-gate"
          >
            <h3 id="gym-terms-consent-heading" className="text-sm font-semibold">
              {GYM_TERMS_COPY.consentHeading}
            </h3>

            {termsBlocked ? (
              <div
                id={TERMS_BLOCKED_ID}
                tabIndex={-1}
                className="mt-2"
                data-testid="gym-terms-blocked"
              >
                {/* Announces itself: it is what answers a press of Pay, and the
                    alert below is deliberately left empty so the sentence is
                    not read out twice. */}
                <p role="status" className="text-muted-foreground text-sm">
                  {termsBlockedMessage}
                </p>
                {termsBlockedUrl && (
                  <a
                    href={termsBlockedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="gym-terms-blocked-link"
                    className="mt-2 inline-block text-sm underline underline-offset-2"
                  >
                    {GYM_TERMS_COPY.linkText}
                  </a>
                )}
                {!termsMultiGym && termsRetries < TERMS_RETRY_LIMIT && (
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="gym-terms-retry"
                      onClick={() => {
                        /* Terms only — refetchReview would re-fetch the consent
                           copy and drop the statutory tick with it. The refusal
                           is kept until the refetch answers: clearing it here
                           would empty the gate and read as "fixed" when the
                           summary is about to say the same thing again. */
                        setTermsRetries((count) => count + 1);
                        setTermsMessage("");
                        setTermsRetrying(true);
                        refetchTerms();
                      }}
                    >
                      {GYM_TERMS_COPY.blockedRetry}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-2 flex flex-row items-start gap-3">
                <Checkbox
                  id={TERMS_CHECKBOX_ID}
                  data-testid="gym-terms-consent-checkbox"
                  className="mt-1"
                  checked={termsConsented}
                  onCheckedChange={(checked) => {
                    setTermsConsented(checked === true);
                    if (checked === true) setTermsMessage("");
                  }}
                  aria-describedby={TERMS_ALERT_ID}
                />
                {/* Block, not the Label default flex-row: the sentence wraps
                    around an inline link and centring it reads as one line. */}
                <Label
                  htmlFor={TERMS_CHECKBOX_ID}
                  data-testid="gym-terms-consent-label"
                  className="block text-sm leading-relaxed font-normal"
                >
                  {GYM_TERMS_COPY.consentLabel}{" "}
                  {/* Exactly one: more than one gym lands in the blocked branch. */}
                  <a
                    href={requiredTerms[0].publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="gym-terms-consent-link"
                    className="underline underline-offset-2"
                    /* A label forwards clicks to its control; reading the terms
                       must never tick the box. */
                    onClick={(event) => event.stopPropagation()}
                  >
                    {GYM_TERMS_COPY.linkText}
                  </a>
                </Label>
              </div>
            )}

            {/* Its own alert, so the two instruments never answer for each other. */}
            <p
              id={TERMS_ALERT_ID}
              role="alert"
              aria-live="assertive"
              data-testid="gym-terms-consent-alert"
              className="text-destructive mt-3 text-sm font-medium empty:mt-0"
            >
              {termsMessage}
            </p>
          </section>
        )}

        {/* Assertive because it answers a press of the pay button: the buyer has
            just tried to act and needs to know why nothing happened. Mounted
            always so it can announce when it fills. */}
        <p
          id={CONSENT_ALERT_ID}
          role="alert"
          aria-live="assertive"
          data-testid="consent-alert"
          className="text-destructive mt-3 text-sm font-medium empty:mt-0"
        >
          {consentMessage}
        </p>
      </div>

      {/* One region for the total, so a screen reader hears the new figure once
          rather than once per line that moved it. */}
      <span className="sr-only" aria-live="polite" data-testid="cart-total-announcement">
        {totalAnnouncement}
      </span>

      <div className="flex flex-row justify-end">
        <Button
          variant="outline"
          onClick={handleCheckout}
          disabled={cartEmpty || cart.processingCheckout}
          data-testid="checkout-submit"
        >
          {cart.processingCheckout && <Spinner className="mr-2" />}
          {`Checkout ${cart.totalPrice} €`}
        </Button>
      </div>
    </MyModal>
  );
};

export default CheckoutModal;
